import { Component } from '@theme/component';
import { QuantitySelectorUpdateEvent, ThemeEvents } from '@theme/events';
import { getCartQuantity } from '@theme/cart-quantity';

/**
 * A custom element that allows the user to select a quantity.
 *
 * @typedef {Object} Refs
 * @property {HTMLInputElement} quantityInput
 * @property {HTMLButtonElement} [decreaseButton]
 * @property {HTMLButtonElement} [increaseButton]
 *
 * @extends {Component<Refs>}
 */
class QuantitySelectorComponent extends Component {
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#abortController;

    document.addEventListener(ThemeEvents.cartUpdate, this.#applyCartAllowance, { signal });
    document.addEventListener(ThemeEvents.variantUpdate, this.#applyCartAllowance, { signal });

    this.#applyCartAllowance();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
  }

  /**
   * Handles the quantity increase event.
   * @param {Event} event - The event.
   */
  increaseQuantity(event) {
    if (!(event.target instanceof HTMLElement)) return;

    event.preventDefault();
    this.refs.quantityInput.stepUp();
    this.#onQuantityChange();
  }

  /**
   * Handles the quantity decrease event.
   * @param {Event} event - The event.
   */
  decreaseQuantity(event) {
    if (!(event.target instanceof HTMLElement)) return;

    event.preventDefault();
    this.refs.quantityInput.stepDown();
    this.#onQuantityChange();
  }

  /**
   * When our input gets focused, we want to fully select the value.
   * @param {FocusEvent} event
   */
  selectInputValue(event) {
    const { quantityInput } = this.refs;
    if (!(event.target instanceof HTMLInputElement) || document.activeElement !== quantityInput) return;

    quantityInput.select();
  }

  /**
   * Handles the quantity set event.
   * @param {Event} event - The event.
   */
  setQuantity(event) {
    if (!(event.target instanceof HTMLElement)) return;

    event.preventDefault();
    if (event.target instanceof HTMLInputElement) {
      this.refs.quantityInput.value = event.target.value;
    }
    this.#onQuantityChange();
  }

  /**
   * Handles the quantity change event.
   */
  #onQuantityChange() {
    const { quantityInput } = this.refs;

    this.#checkQuantityRules();
    this.#updateDecreaseButtonStatus();
    this.#updateIncreaseButtonStatus();
    const newValue = parseInt(quantityInput.value);

    quantityInput.dispatchEvent(new QuantitySelectorUpdateEvent(newValue, Number(quantityInput.dataset.cartLine)));
  }

  /**
   * Lowers the maximum by whatever the customer already has in their cart, so a product limited to
   * six of which two are in the cart can only have four more added.
   */
  #applyCartAllowance = async () => {
    const limit = parseInt(this.dataset.maxQuantity ?? '', 10);
    if (!limit) return;

    const remaining = Math.max(limit - (await getCartQuantity(this.dataset.variantId)), 0);
    const limitReached = remaining === 0;
    const { quantityInput } = this.refs;

    quantityInput.max = String(remaining);
    this.toggleAttribute('data-limit-reached', limitReached);
    this.#toggleAddToCart(limitReached);

    // Leave the displayed quantity alone once the limit is used up - showing a 0 next to a disabled
    // add to cart button reads as broken rather than as a limit.
    if (limitReached) {
      quantityInput.toggleAttribute('data-limit-disabled', true);
      quantityInput.disabled = true;
    } else {
      if (quantityInput.hasAttribute('data-limit-disabled')) {
        quantityInput.removeAttribute('data-limit-disabled');
        quantityInput.disabled = false;
      }
      this.#checkQuantityRules();
    }

    this.#updateDecreaseButtonStatus();
    this.#updateIncreaseButtonStatus();
  };

  /**
   * Blocks the add to cart button of the surrounding form once the limit is used up. Only buttons
   * this component disabled itself are re-enabled, so sold out products stay disabled.
   * @param {boolean} limitReached - Whether the customer has reached the limit.
   */
  #toggleAddToCart(limitReached) {
    const addToCart = this.closest('product-form-component')?.querySelector('add-to-cart-component');
    if (!(addToCart instanceof HTMLElement)) return;

    if (limitReached) {
      addToCart.toggleAttribute('data-limit-disabled', true);
      // @ts-expect-error - AddToCartComponent exposes disable()
      addToCart.disable?.();
    } else if (addToCart.hasAttribute('data-limit-disabled')) {
      addToCart.removeAttribute('data-limit-disabled');
      // @ts-expect-error - AddToCartComponent exposes enable()
      addToCart.enable?.();
    }
  }

  /**
   * Checks the quantity rules are met
   */
  #checkQuantityRules = () => {
    const { quantityInput } = this.refs;
    const newValue = Number(quantityInput.value);
    const min = Number(quantityInput.min);
    const max = Number(quantityInput.max);

    if (quantityInput.min && newValue < min) quantityInput.value = String(min);
    if (quantityInput.max && newValue > max) quantityInput.value = String(max);
  };

  /**
   * Disables the Decrease button if the quantity is 1
   */
  #updateDecreaseButtonStatus() {
    const { quantityInput, decreaseButton } = this.refs;

    if (!decreaseButton) return;

    if (parseInt(quantityInput.value) <= 1) {
      decreaseButton.style.opacity = '0.5';
      decreaseButton.style.cursor = 'not-allowed';
      decreaseButton.disabled = true;
    } else {
      decreaseButton.style.opacity = '';
      decreaseButton.style.cursor = '';
      decreaseButton.disabled = false;
    }
  }

  /**
   * Disables the Increase button once the maximum allowed quantity is reached
   */
  #updateIncreaseButtonStatus() {
    const { quantityInput, increaseButton } = this.refs;

    if (!increaseButton || !quantityInput.max) return;

    const max = Number(quantityInput.max);

    if (max <= 0 || parseInt(quantityInput.value) >= max) {
      increaseButton.style.opacity = '0.5';
      increaseButton.style.cursor = 'not-allowed';
      increaseButton.disabled = true;
    } else {
      increaseButton.style.opacity = '';
      increaseButton.style.cursor = '';
      increaseButton.disabled = false;
    }
  }

  /**
   * Gets the quantity input.
   * @returns {HTMLInputElement} The quantity input.
   */
  get quantityInput() {
    if (!this.refs.quantityInput) {
      throw new Error('Missing <input ref="quantityInput" /> inside <quantity-selector-component />');
    }

    return this.refs.quantityInput;
  }
}

if (!customElements.get('quantity-selector-component')) {
  customElements.define('quantity-selector-component', QuantitySelectorComponent);
}
